<template>
    <div v-show="stepActive" class="tournament-rules">
        <h2 class="title">{{ $t('title_TournamentRules') }}</h2>
        <p class="subtitle">{{ $t('subtitle_TournamentRules') }}</p>

        <div class="info-card">
            <v-form>
                <!-- Initial Balance Field (when selfManaged is true) -->
                <div v-if="true" class="rule-container mb-4">
                    <div class="d-flex align-center">
                        <div class="rule-input">
                            <div class="input-container">
                                <div class="status-icon mr-2">
                                    <i v-if="!initialBalanceIsDirty" class="fa-regular fa-circle" style="color: #9A9A9A;"></i>
                                    <i v-else-if="!initialBalanceError" class="fa-regular fa-circle-check" style="color: var(--v-mainGreen-base);"></i>
                                    <i v-else class="fa-regular fa-circle-xmark" style="color: #FF3B3B;"></i>
                                </div>
                                <label class="input-label">{{ initialBalanceLabel }}</label>
                                <div class="input-wrapper">
                                    <!-- View Mode -->
                                    <div v-if="!isInitialBalanceEditing && !initialBalanceError && initialBalanceIsDirty" 
                                        class="value-display currency-input"
                                        @click="startEditingInitialBalance">
                                        <span class="value">${{ initialBalance }}</span>
                                        <v-icon small class="edit-icon ml-2">fa-regular fa-pen</v-icon>
                                    </div>
                                    <!-- Edit Mode -->
                                    <v-text-field v-else
                                        v-model="initialBalanceInput"
                                        :error-messages="initialBalanceIsDirty ? initialBalanceError : ''"
                                        class="currency-input"
                                        type="number"
                                        outlined
                                        dense
                                        prefix="$"
                                        @input="onInitialBalanceInput"
                                        @blur="handleInitialBalanceBlur"
                                        @keyup.enter="handleInitialBalanceBlur"
                                        hide-details="auto">
                                    </v-text-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <transition-group name="rule-animation">
                    <div v-for="(rule, index) in rules" :key="index" class="rule-container mb-4">
                        <!-- Existing Rule Display -->
                        <div v-if="rule.type" class="d-flex align-center">
                            <div class="rule-input">
                                <div class="input-container">
                                    <div class="status-icon mr-2">
                                        <!-- Special handling for overnight rule -->
                                        <template v-if="isToggleType(rule.type)">
                                            <i class="fa-regular fa-circle-check" style="color: var(--v-mainGreen-base);"></i>
                                        </template>
                                        <template v-else>
                                            <i v-if="!rule.isDirty" class="fa-regular fa-circle" style="color: #9A9A9A;"></i>
                                            <i v-else-if="!rule.error" class="fa-regular fa-circle-check" style="color: var(--v-mainGreen-base);"></i>
                                            <i v-else class="fa-regular fa-circle-xmark" style="color: #FF3B3B;"></i>
                                        </template>
                                    </div>
                                    <label class="input-label">{{ getRuleLabel(rule.type) }}</label>
                                    <div class="input-wrapper">
                                        <template v-if="!isToggleType(rule.type)">
                                            <!-- View Mode -->
                                            <div v-if="!rule.isEditing && !rule.error && rule.isDirty" 
                                                class="value-display" 
                                                :class="getInputClass(rule.type)"
                                                @click="startEditing(index)">
                                                <span class="value">
                                                    {{ getCurrencyPrefix(rule.type) }}{{ getDisplayValue(rule) }}{{ getSuffix(rule.type) }}
                                                </span>
                                                <v-icon small class="edit-icon ml-2">fa-regular fa-pen</v-icon>
                                            </div>
                                            <!-- Edit Mode -->
                                            <v-text-field v-else
                                                :value="getEditValue(rule)"
                                                @input="handleRuleInput(index, $event)"
                                                :error-messages="rule.isDirty ? getErrorMessage(rule) : ''"
                                                :class="getInputClass(rule.type)"
                                                outlined
                                                dense
                                                :type="getInputType(rule.type)"
                                                :prefix="getCurrencyPrefix(rule.type)"
                                                :suffix="getSuffix(rule.type)"
                                                @blur="handleBlur(index)"
                                                @keyup.enter="handleBlur(index)"
                                                hide-details="auto">
                                                <template v-slot:message="{ message }">
                                                    <div class="error-message">{{ message }}</div>
                                                </template>
                                            </v-text-field>
                                        </template>
                                        <div v-else class="overnight-rule" style="display: none;">
                                            <i class="fa-regular fa-check" style="color: var(--v-mainGreen-base); font-size: 20px;"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <v-btn icon small class="action-btn delete-btn ml-auto" @click="removeRule(index)">
                                <i class="fa-regular fa-trash" style="color: white !important;"></i>
                            </v-btn>
                            <v-btn v-if="!isLastRuleSelected && index === rules.length - 1 && !showNewRuleSelect" 
                                icon small class="action-btn add-btn ml-2" @click="showNewRuleSelect = true">
                                <i class="fa-regular fa-plus" style="color: black;"></i>
                            </v-btn>
                        </div>
                    </div>
                </transition-group>

                <!-- New Rule Selector -->
                <div v-if="showNewRuleSelect || rules.length === 0" class="rule-select-container">
                    <label class="select-label">{{ $t(rules.length === 0 ? 'selectFirst' : 'selectNext') }}</label>
                    <div class="d-flex align-center flex-grow-1">
                        <v-select v-model="selectedRule" :items="availableRules" item-text="label" item-value="type"
                            outlined dense @change="addRule" class="mr-4"></v-select>
                        <v-btn v-if="rules.length > 0" icon small class="action-btn delete-btn" @click="cancelNewRule">
                            <i class="fa-regular fa-trash" style="color: white !important;"></i>
                        </v-btn>
                    </div>
                </div>
            </v-form>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import TournamentRepository from '@/repositories/tournament';

const dynamicRuleLabelKeys = {
  CONSISTENCY: 'rules_TournamentRules.dynamic.consistency',
  MAX_AGGREGATED_TRADE_SIZE: 'rules_TournamentRules.dynamic.maxAggregatedTradeSize',
  MAX_DRAWDOWN_BL_PT: 'rules_TournamentRules.dynamic.maxBalanceDrawdownPercent',
  MAX_DRAWDOWN_EQ_PT: 'rules_TournamentRules.dynamic.maxEquityDrawdownPercent',
  MAX_TRADE_SIZE: 'rules_TournamentRules.dynamic.maxTradeSizeLots',
  MIN_AGGREGATED_TRADE_SIZE: 'rules_TournamentRules.dynamic.minAggregatedTradeSizeLots',
  MIN_TRADABLE_DAYS: 'rules_TournamentRules.dynamic.minTradingDaysDays',
  MIN_TRADES: 'rules_TournamentRules.dynamic.minTrades',
  MIN_TRADE_SIZE: 'rules_TournamentRules.dynamic.minTradeSizeLots',
  OVERNIGHT: 'rules_TournamentRules.dynamic.overnightRestriction',
  POSITIVE_BALANCE: 'rules_TournamentRules.dynamic.positiveBalance',
  POSITIVE_EQUITY: 'rules_TournamentRules.dynamic.positiveEquity'
};

export default {
    name: 'TournamentRules',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        },
        clubId: {
            type: Number,
            required: false
        }
    },
    data: () => ({
        showNewRuleSelect: false,
        selectedRule: null,
        ruleTypes: [
            { type: 'INITIAL_BALANCE', label: 'rules_TournamentRules.initialBalance' },
            { type: 'MIN_TRADING_DAYS', label: 'rules_TournamentRules.minTradingDays' },
            { type: 'CONSISTENCY', label: 'rules_TournamentRules.consistency' },
            { type: 'MAX_TRADE_SIZE', label: 'rules_TournamentRules.maxTradeSize' },
            { type: 'MAX_LOSS_PER_TRADE', label: 'rules_TournamentRules.maxLossPerTrade' },
            { type: 'MAX_DRAWDOWN', label: 'rules_TournamentRules.maxDrawdown' },
            { type: 'OVERNIGHT', label: 'rules_TournamentRules.overnight' }
        ],
        availableRuleTypes: [],
        initialBalanceInput: '',
        initialBalanceIsDirty: false,
        initialBalanceError: '',
        isInitialBalanceEditing: false, // Changed from true to false
        initialBalanceOriginalValue: '',
        _previousRankingType: null // Add this to track ranking type changes
    }),
    computed: {
        ...mapState({
            rules: state => state.tournamentForm.rules,
            basicInfo: state => state.tournamentForm.basicInfo,
            rankingType: state => state.tournamentForm.participants.rankingType,
            selfManaged: state => state.tournamentForm.details.selfManaged,
            stateInitialBalance: state => state.tournamentForm.initialBalance,
            tournamentType: state => state.tournamentForm.details.tournamentType
        }),
        initialBalance: {
            get() {
                return this.stateInitialBalance;
            },
            set(value) {
                this.updateTournamentInitialBalance(value);
            }
        },
        // Add new computed property
        tournamentDurationInDays() {
            if (!this.basicInfo.startDate || !this.basicInfo.endDate) return 0;
            const start = new Date(this.basicInfo.startDate);
            const end = new Date(this.basicInfo.endDate);
            return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        },
        // Modify availableRules to use filtered ruleTypes
        availableRules() {
            return this.availableRuleTypes
                .map(type => {
                    // First check in dynamic rules
                    const dynamicRule = this.dynamicRuleTypes?.find(rt => rt.type === type);
                    if (dynamicRule) {
                        return dynamicRule;
                    }
                    // Fall back to static rules
                    return this.ruleTypes.find(rt => rt.type === type);
                })
                .filter(type => type && !this.rules.some(rule => rule.type === type.type))
                .map(type => ({
                    type: type.type,
                    label: this.dynamicRuleTypes?.find(r => r.type === type.type)
                        ? type.label // Dynamic rules already have the label
                        : this.$t(type.label) // Static rules need translation
                }));
        },
        isLastRuleSelected() {
            return this.availableRules.length === 0;
        },
        shouldShowInitialBalance() {
            return this.selfManaged;
        },
        initialBalanceLabel() {
            // For DEMO competitions: "Initial Balance"
            // For LIVE competitions: "Minimum Required Balance"
            if (this.tournamentType === 'DEMO') {
                return this.$t('rules_TournamentRules.initialBalance');
            } else if (this.tournamentType === 'LIVE') {
                return this.$t('rules_TournamentRules.minimumRequiredBalance');
            }
            // Default fallback
            return this.$t('rules_TournamentRules.initialBalance');
        }
    },
    methods: {
        ...mapMutations(['updateTournamentRules', 'updateTournamentInitialBalance']),
        addRule() {
            if (this.selectedRule) {
                // Try to find in dynamic rules first, then fall back to static
                const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === this.selectedRule);
                const ruleType = dynamicRule || this.ruleTypes.find(t => t.type === this.selectedRule);

                // Set default value based on dataType or specific rule type
                let defaultValue = '';

                // Force MAX_DRAWDOWN_RT to use percentage default
                if (this.selectedRule === 'MAX_DRAWDOWN_RT') {
                    defaultValue = '0.3'; // Default 30%
                } else if (dynamicRule) {
                    if (dynamicRule.dataType === 'BOOLEAN') {
                        defaultValue = true;
                    } else if (dynamicRule.dataType === 'PERCENTAGE') {
                        defaultValue = '0.3'; // Default 30%
                    } else if (dynamicRule.dataType === 'FLOAT') {
                        defaultValue = '1000'; // Default value for float
                    }
                } else {
                    // Fallback for static rules
                    defaultValue = ruleType.type === 'OVERNIGHT' ? true : (ruleType.defaultValue || '');
                }

                const newRule = {
                    type: this.selectedRule,
                    value: defaultValue,
                    error: '',
                    isDirty: false,
                    isEditing: true // Add this
                };
                this.updateTournamentRules([...this.rules, newRule]);
                // Validate immediately after adding
                this.validateAllRules();
                this.selectedRule = null;
                this.showNewRuleSelect = false;
            }
        },
        validateAllRules() {
            if (this.rules.length === 0 && !this.shouldShowInitialBalance) {
                this.setNextBtnDisabled(false);
                return true;
            }

            let isValid = true;

            // Always validate initial balance if it's required
            if (this.shouldShowInitialBalance) {
                const value = parseFloat(this.initialBalance);
                isValid = value && !isNaN(value) && value > 0;
            }

            // Validate other rules
            isValid = isValid && this.rules.every((rule) => {
                if (!this.isToggleType(rule.type)) {
                    if (!rule.value || rule.value.trim() === '') {
                        rule.error = this.$t('errors.required');
                        return false;
                    }

                    if (this.isNumericType(rule.type)) {
                        const numValue = parseFloat(rule.value);

                        // Check if this is a percentage type from dynamic rules or specific rule types
                        const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === rule.type);
                        const isPercentage = dynamicRule?.dataType === 'PERCENTAGE' ||
                                           rule.type === 'MAX_DRAWDOWN_RT' ||
                                           rule.type === 'CONSISTENCY';

                        if (isNaN(numValue)) {
                            rule.error = this.$t('errors.invalidNumber');
                            return false;
                        }

                        // For percentages, value should be between 0.01 and 0.9 (1% and 90%)
                        if (isPercentage) {
                            if (numValue < 0.01 || numValue > 0.9) {
                                rule.error = this.$t('errors.percentageRange');
                                return false;
                            }
                        } else if (numValue <= 0) {
                            rule.error = this.$t('errors.greaterThanZero');
                            return false;
                        }

                        // MIN_TRADING_DAYS specific validation
                        if (rule.type === 'MIN_TRADING_DAYS') {
                            const duration = this.tournamentDurationInDays;
                            if (duration > 0 && numValue > duration) {
                                rule.error = this.$t('errors.exceedsTournamentDuration', { 
                                    days: duration 
                                });
                                return false;
                            }
                        }
                        
                        // Remove old percentage validation since we handle it above

                        // Validate against min/max from dynamic rules if available
                        // Skip for MAX_DRAWDOWN_RT since we force it to use percentage validation
                        if (dynamicRule && rule.type !== 'MAX_DRAWDOWN_RT') {
                            if (dynamicRule.minValue !== null && numValue < dynamicRule.minValue) {
                                rule.error = `Minimum value is ${dynamicRule.minValue}`;
                                return false;
                            }
                            if (dynamicRule.maxValue !== null && numValue > dynamicRule.maxValue) {
                                rule.error = `Maximum value is ${dynamicRule.maxValue}`;
                                return false;
                            }
                        }
                    }
                }
                return true;
            });

            // Update button state
            this.setNextBtnDisabled(!isValid);
            
            // Force component update to show errors
            this.$forceUpdate();
            
            return isValid;
        },

        validateRule(index) {
            const rule = this.rules[index];
            if (!rule || !rule.type) return false;

            // Clear previous error
            rule.error = '';
            rule.isDirty = true;

            if (!rule.value || rule.value.trim() === '') {
                rule.error = this.$t('errors.required');
                this.validateAllRules();
                return false;
            }

            if (this.isNumericType(rule.type)) {
                const numValue = parseFloat(rule.value);

                // Check if this is a percentage type from dynamic rules or specific rule types
                const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === rule.type);
                const isPercentage = dynamicRule?.dataType === 'PERCENTAGE' ||
                                   rule.type === 'MAX_DRAWDOWN_RT' ||
                                   rule.type === 'CONSISTENCY';

                if (isNaN(numValue)) {
                    rule.error = this.$t('errors.invalidNumber');
                    this.validateAllRules();
                    return false;
                }

                // For percentages, value should be between 0.01 and 0.9 (1% and 90%)
                if (isPercentage) {
                    if (numValue < 0.01 || numValue > 0.9) {
                        rule.error = this.$t('errors.percentageRange');
                        this.validateAllRules();
                        return false;
                    }
                } else if (numValue <= 0) {
                    rule.error = this.$t('errors.greaterThanZero');
                    this.validateAllRules();
                    return false;
                }

                // MIN_TRADING_DAYS specific validation
                if (rule.type === 'MIN_TRADING_DAYS') {
                    const duration = this.tournamentDurationInDays;
                    if (duration > 0 && numValue > duration) {
                        rule.error = this.$t('errors.exceedsTournamentDuration', {
                            days: duration
                        });
                        this.validateAllRules();
                        return false;
                    }
                }

                // Validate against min/max from dynamic rules if available
                // Skip for MAX_DRAWDOWN_RT since we force it to use percentage validation
                if (dynamicRule && rule.type !== 'MAX_DRAWDOWN_RT') {
                    if (dynamicRule.minValue !== null && numValue < dynamicRule.minValue) {
                        rule.error = `Minimum value is ${dynamicRule.minValue}`;
                        this.validateAllRules();
                        return false;
                    }
                    if (dynamicRule.maxValue !== null && numValue > dynamicRule.maxValue) {
                        rule.error = `Maximum value is ${dynamicRule.maxValue}`;
                        this.validateAllRules();
                        return false;
                    }
                }

                // Update the value with the parsed number
                rule.value = numValue.toString();
            }

            this.validateAllRules();
            return true;
        },

        handleInput(index) {
            if (this._inputTimeout) {
                clearTimeout(this._inputTimeout);
            }

            this._inputTimeout = setTimeout(() => {
                const rule = this.rules[index];
                if (!rule) return;

                rule.isDirty = true;
                this.validateRule(index);
                this.$forceUpdate(); // Force update to ensure error display
            }, 300);
        },
        handleRuleInput(index, value) {
            const rule = this.rules[index];
            if (!rule) return;

            // Check if this is a percentage type from dynamic rules or specific rule types
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === rule.type);
            const isPercentage = dynamicRule?.dataType === 'PERCENTAGE' ||
                               rule.type === 'MAX_DRAWDOWN_RT' ||
                               rule.type === 'CONSISTENCY';

            if (isPercentage) {
                // Convert percentage input to decimal (30 -> 0.3)
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                    rule.value = (numValue / 100).toString();
                } else {
                    rule.value = value;
                }
            } else {
                rule.value = value;
            }

            this.handleInput(index);
        },
        getDisplayValue(rule) {
            // Check if this is a percentage type from dynamic rules or specific rule types
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === rule.type);
            const isPercentage = dynamicRule?.dataType === 'PERCENTAGE' ||
                               rule.type === 'MAX_DRAWDOWN_RT' ||
                               rule.type === 'CONSISTENCY';

            if (isPercentage) {
                // Convert decimal to percentage for display (0.3 -> 30)
                const numValue = parseFloat(rule.value);
                if (!isNaN(numValue)) {
                    return (numValue * 100).toFixed(0);
                }
            }
            return rule.value;
        },
        getEditValue(rule) {
            // Same as display value for editing
            return this.getDisplayValue(rule);
        },
        removeRule(index) {
            // Don't validate if removing the last remaining rule
            const isLastRule = this.rules.length === 1;
            const newRules = [...this.rules];
            newRules.splice(index, 1);
            this.updateTournamentRules(newRules);
            
            // Show the rule selector if we just removed the last rule
            if (isLastRule) {
                this.showNewRuleSelect = true;
            } else {
                this.showNewRuleSelect = false;
                this.$nextTick(() => {
                    this.validateAllRules();
                });
            }
        },
        isToggleType(type) {
            // Check dynamic rules for BOOLEAN dataType
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule) {
                return dynamicRule.dataType === 'BOOLEAN';
            }
            // Fallback for static rules
            return type === 'OVERNIGHT';
        },
        getInputType(type) {
            // Force MAX_DRAWDOWN_RT and CONSISTENCY to use number input
            if (type === 'MAX_DRAWDOWN_RT' || type === 'CONSISTENCY') {
                return 'number';
            }

            // Check dynamic rules for dataType
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule) {
                if (dynamicRule.dataType === 'PERCENTAGE' || dynamicRule.dataType === 'FLOAT') {
                    return 'number';
                }
            }
            // Fallback for static rules
            return ['MAX_DRAWDOWN'].includes(type) ? 'number' : 'text';
        },
        getCurrencyPrefix(type) {
            // Check if it's MAX_DRAWDOWN with FLOAT type (absolute value)
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule && type === 'MAX_DRAWDOWN' && dynamicRule.dataType === 'FLOAT') {
                return '$'; // Absolute drawdown in currency
            }
            // Original currency types (MAX_TRADE_SIZE removed - it's lots/contracts, not currency)
            return ['INITIAL_BALANCE', 'MAX_LOSS_PER_TRADE'].includes(type) ? '$' : '';
        },
        getSuffix(type) {
            // Check if this is a percentage type from dynamic rules or specific rule types
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);

            // Force MAX_DRAWDOWN_RT and CONSISTENCY to show % suffix
            if (type === 'MAX_DRAWDOWN_RT' || type === 'CONSISTENCY') {
                return '%';
            }

            if (dynamicRule) {
                if (dynamicRule.dataType === 'PERCENTAGE') {
                    return '%';
                }
                // For FLOAT type MAX_DRAWDOWN, no suffix (currency prefix is used)
                if (type === 'MAX_DRAWDOWN' && dynamicRule.dataType === 'FLOAT') {
                    return '';
                }
            }
            // Static rules
            if (type === 'MIN_TRADING_DAYS') return this.$t('suffix.days');
            return '';
        },
        getRuleLabel(type) {
            // Check dynamic rules first
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule) {
                return dynamicRule.label;
            }
            // Fall back to static rules
            const ruleType = this.ruleTypes.find(t => t.type === type);
            return ruleType ? this.$t(ruleType.label) : '';
        },
        handleBlur(index) {
            const rule = this.rules[index];
            const originalValue = rule.value;
            
            rule.isDirty = true;
            if (this.validateRule(index)) {
                // Only keep in edit mode if the value actually changed
                const hasValueChanged = originalValue !== rule.value;
                rule.isEditing = hasValueChanged;
            }
        },
        getErrorMessage(rule) {
            return rule.error || '';
        },
        isNumericType(type) {
            // Check dynamic rules for numeric dataTypes
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule) {
                return dynamicRule.dataType === 'PERCENTAGE' || dynamicRule.dataType === 'FLOAT';
            }
            // Fallback for static rules and specific rule types
            return [
                'INITIAL_BALANCE',
                'MIN_TRADING_DAYS',
                'CONSISTENCY',
                'MAX_TRADE_SIZE',
                'MAX_LOSS_PER_TRADE',
                'MAX_DRAWDOWN_RT'
            ].includes(type);
        },
        cancelNewRule() {
            this.showNewRuleSelect = false;
            this.selectedRule = null;
        },
        getInputClass(type) {
            // Force MAX_DRAWDOWN_RT and CONSISTENCY to use percentage-input class
            if (type === 'MAX_DRAWDOWN_RT' || type === 'CONSISTENCY') {
                return 'percentage-input';
            }

            // Check dynamic rules for dataType
            const dynamicRule = this.dynamicRuleTypes?.find(r => r.type === type);
            if (dynamicRule) {
                if (dynamicRule.dataType === 'PERCENTAGE') {
                    return 'percentage-input';
                }
                if (dynamicRule.dataType === 'FLOAT' && type === 'MAX_DRAWDOWN') {
                    return 'currency-input'; // Absolute drawdown
                }
            }
            // Static rules (MAX_TRADE_SIZE removed - it's lots/contracts, not currency)
            if (['INITIAL_BALANCE', 'MAX_LOSS_PER_TRADE'].includes(type)) {
                return 'currency-input';
            }
            if (['MIN_TRADING_DAYS'].includes(type)) {
                return 'days-input';
            }
            return '';
        },
        startEditing(index) {
            const rule = this.rules[index];
            rule.isEditing = true;
            // Store original value to compare later
            rule._originalValue = rule.value;
        },
        async fetchAvailableRules() {
            const platformInfo = this.$store.state.tournamentPlatformInfo;
            // Use selectedClubId from form state first (broker app selection), then clubMember, then prop
            const clubId = this.$store.state.tournamentForm?.basicInfo?.selectedClubId
                || this.$store.state.clubMember?.club?.id
                || this.clubId;

            // Check for platform and either platformGroupId or mtGroupId
            const groupId = platformInfo.platformGroupId || platformInfo.mtGroupId;

            if (platformInfo.platform) {
                this.loading = true;
                try {
                    // Use new wizard rules endpoint with either platformGroupId or mtGroupId
                    const response = await TournamentRepository.getWizardRules(
                        clubId,
                        platformInfo.platform,
                        groupId
                    );

                    // Transform API response to match our rule format
                    this.dynamicRuleTypes = response.data.map(rule =>{
                        const labelKey = dynamicRuleLabelKeys[rule.ruleType];

                        return{
                        type: rule.ruleType,
                        label: labelKey ? this.$t(labelKey) : rule.name,
                        dataType: rule.dataType,
                        minValue: rule.minValue,
                        maxValue: rule.maxValue,
                        description: rule.description
                        }; 
                    });

                    // Set available rule types from dynamic rules
                    this.availableRuleTypes = this.dynamicRuleTypes.map(r => r.type);

                    // Filter out INITIAL_BALANCE if selfManaged is true
                    if (this.selfManaged) {
                        this.availableRuleTypes = this.availableRuleTypes.filter(type => type !== 'INITIAL_BALANCE');
                    }

                    this.showNewRuleSelect = this.rules.length === 0;
                } catch (error) {
                    /* eslint-disable no-console */
                    console.error('Error fetching wizard rules:', error);
                    /* eslint-enable no-console */
                    this.availableRuleTypes = [];
                } finally {
                    this.loading = false;
                }
            } else {
                // No platform info available - set empty rules
                /* eslint-disable no-console */
                console.warn('Platform information (platform and groupId) is required for wizard rules');
                /* eslint-enable no-console */
                this.availableRuleTypes = [];
            }
        },
        onInitialBalanceInput(value) {
            this.initialBalanceInput = value.replace(/[^0-9]/g, '');
            const isValid = this.validateInitialBalance();
            // Immediately update disabled state based on validation
            this.setNextBtnDisabled(!isValid);
        },
        validateInitialBalance() {
            this.initialBalanceIsDirty = true;
            const value = parseFloat(this.initialBalanceInput || this.initialBalance);
            
            if (!this.initialBalanceInput || this.initialBalanceInput.trim() === '') {
                this.initialBalanceError = this.$t('errors.required');
                return false;
            }
            
            if (isNaN(value) || value <= 0) {
                this.initialBalanceError = this.$t('errors.greaterThanZero');
                return false;
            }

            this.initialBalanceError = '';
            return true;
        },
        startEditingInitialBalance() {
            // Set the input value before enabling edit mode
            this.initialBalanceInput = this.initialBalance;
            this.isInitialBalanceEditing = true;
        },
        handleInitialBalanceBlur() {
            const isValid = this.validateInitialBalance();
            if (isValid) {
                this.updateTournamentInitialBalance(this.initialBalanceInput);
                this.isInitialBalanceEditing = false;
            } else {
                // If invalid, keep in edit mode and show error
                this.isInitialBalanceEditing = true;
                // Clear the stored value to ensure validation fails
                this.updateTournamentInitialBalance('');
            }
            this.validateAllRules();
        }
    },
    async created() {
        this._previousRankingType = this.rankingType; // Initialize the previous ranking type
        await this.fetchAvailableRules();
        // Fix initialization of initial balance state
        if (this.shouldShowInitialBalance) {
            if (this.initialBalance) {
                this.initialBalanceInput = this.initialBalance;
                this.initialBalanceIsDirty = true;
                this.isInitialBalanceEditing = false;
                this.validateInitialBalance();
            } else {
                this.isInitialBalanceEditing = true;
            }
        }
        this.validateForm();

        // Watch for platform info changes
        this.$watch(
            () => this.$store.state.tournamentPlatformInfo.platform,
            async () => {
                await this.fetchAvailableRules();
            },
            { deep: true }
        );
    },
    beforeDestroy() {
        // Clean up any pending timeouts
        if (this._inputTimeout) {
            clearTimeout(this._inputTimeout);
        }
    },
    watch: {
        // Add watcher for rules array
        rules: {
            deep: true,
            immediate: true,
            handler() {
                this.$nextTick(() => {
                    this.validateAllRules();
                });
            }
        },
        // Add watcher for tournament duration changes
        tournamentDurationInDays: {
            handler() {
                this.$nextTick(() => {
                    this.validateAllRules();
                });
            }
        },
        rankingType: {
            handler(newVal, oldVal) {
                if (oldVal && newVal !== oldVal) {
                    this.fetchAvailableRules();
                }
            },
            immediate: true
        },
        selfManaged: {
            handler(newVal) {
                if (newVal) {
                    this.isInitialBalanceEditing = true;
                    this.initialBalanceInput = '';
                    this.initialBalanceIsDirty = false;
                    this.initialBalanceError = '';
                    // Remove INITIAL_BALANCE from rules if it exists
                    const newRules = this.rules.filter(rule => rule.type !== 'INITIAL_BALANCE');
                    if (newRules.length !== this.rules.length) {
                        this.updateTournamentRules(newRules);
                    }
                }
                this.validateAllRules();
            },
            immediate: true
        },
        initialBalance: {
            handler(newVal) {
                if (this.shouldShowInitialBalance) {
                    const value = parseFloat(newVal);
                    const isValid = value && !isNaN(value) && value > 0;
                    this.setNextBtnDisabled(!isValid);
                }
            },
            immediate: true
        }
    }
}
</script>



<style lang="scss" scoped>
.tournament-rules {
    width: 100%;
    font-family: "Avenir", sans-serif !important;
    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
        font-family: "Avenir", sans-serif !important;
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px 50px 60px;
        overflow-y: auto;

        .v-form {
            max-width: 720px;
            margin: auto;
        }
    }

    .rule-container {
        .v-btn.v-btn--icon {
            width: 36px;
            height: 36px;
            border-radius: 50%;
        }

        .action-btn {
            width: 36px !important;
            height: 36px !important;
            border-radius: 8px !important;
            margin-bottom: 16px;
            padding: 10px 28px;

            &.delete-btn {
                background-color: #9A9A9A !important;
                margin-left: auto;
            }

            &.add-btn {
                background-color: var(--v-mainGreen-base) !important;
            }
        }
    }

    .rule-select-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-top: 16px;

        .select-label {
            color: var(--v-textSecondary-base);
            font-size: 14px;
            margin-bottom: 25px;
            margin-right: 10px;
            display: block;
        }

        .v-select {
            flex-grow: 1;
        }

        .action-btn {
            width: 36px !important;
            height: 36px !important;
            border-radius: 8px !important;
            margin-bottom: 25px;
            padding: 10px 28px;

            &.delete-btn {
                background-color: #9A9A9A !important;
            }
        }
    }

    .input-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: 16px;

        .status-icon {
            font-size: 16px;
            display: flex;
            align-items: center;
            min-width: 20px;
            justify-content: center;
            margin-top: 2px; // Align with the label
        }

        .input-label {
            color: var(--v-textSecondary-base);
            font-size: 14px;
            min-width: 150px;
            margin-right: 16px;
            margin-bottom: 0;
        }

        .input-wrapper {
            flex-grow: 0; // Change from flex-grow: 1
            width: auto;
            margin-right: auto; // This will push the delete button to the right
            
            // Specific input type widths
            .currency-input {
                width: 100px;
            }

            .days-input {
                width: 100px;
            }

            .percentage-input {
                width: 100px;
            }

            // Override Vuetify's default width
            ::v-deep .v-text-field {
                max-width: 100%;
            }

            // Add styles for error message
            ::v-deep .v-text-field {
                .v-messages {
                    min-width: 250px;
                    position: absolute;
                    left: 0;
                    white-space: nowrap;
                    overflow: visible;
                }

                .error-message {
                    white-space: nowrap;
                    overflow: visible;
                }

                // Adjust input padding to prevent error message overlap
                &.v-text-field--error {
                    margin-bottom: 20px;
                }
            }

            .value-display {
                height: 40px;
                padding: 0 12px;
                border-radius: 4px;
                border: 1px solid transparent; // Changed to transparent by default
                background-color: var(--v-cardBackground-base);
                display: flex;
                align-items: center;
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                    border-color: var(--v-primary-base);
                    background-color: rgba(0, 195, 97, 0.05);
                    
                    .edit-icon {
                        opacity: 1;
                    }
                }

                .value {
                    color: var(--v-textPrimary-base);
                    font-weight: 500;
                }

                .edit-icon {
                    color: var(--v-mainGreen-base);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                // Maintain the same widths as inputs
                &.currency-input {
                    width: 100px;
                }

                &.days-input {
                    width: 100px;
                }

                &.percentage-input {
                    width: 100px;
                }
            }
        }

        .v-input {
            flex-grow: 1;
        }
    }
}

::v-deep .v-input__icon--append .v-icon {
    font-size: 14px;
    color: #9A9A9A;
}

::v-deep .v-input--switch {
    .v-input--selection-controls__input {
        .v-input--switch__track {
            height: 24px !important;
            width: 45px !important;
            top: calc(50% - 12px) !important;
            border-radius: 12px !important;
        }

        .v-input--switch__thumb {
            height: 20px !important;
            margin-left: 5px;
            width: 20px !important;
            top: calc(50% - 10px) !important;
        }
    }

    // Adjust margins for label alignment
    .v-input--selection-controls__input {
        margin-right: 18px;
    }

    // Adjust thumb position when switched on
    &.v-input--is-active {
        .v-input--switch__thumb {
            transform: translate(20px) !important;
        }

        // Add green color when active
        .v-input--switch__track {
            background-color: var(--v-mainGreen-base) !important;
            opacity: 1 !important;
        }
    }

    .v-input--selection-controls__label {
        color: var(--v-mainBlack-base) !important;
    }

    .v-label.theme--light {
        color: var(--v-mainBlack-base) !important;
    }
}

// Add animation styles
.rule-animation-enter-active {
    animation: slide-fade-in 0.3s ease-out;
}

.rule-animation-leave-active {
    animation: slide-fade-out 0.3s ease-in;
}

@keyframes slide-fade-in {
    from {
        transform: translateY(-20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes slide-fade-out {
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(-20px);
        opacity: 0;
    }
}

.overnight-rule {
    display: flex;
    align-items: center;
    height: 40px; // Match the height of other inputs
    padding: 0 12px;
    background-color: var(--v-cardBackground-base);
    border-radius: 4px;
}
</style>
